import { IUserCount, IUserCountResponse, Single } from 'infrastructure/api'

import { getAPIRequest } from 'infrastructure/api'
import { useCallback } from 'react'
import { format, parse } from 'date-fns'

export const LockedSchoolsIds = [1102828]

export async function GetUserRoleCount(id: string | number, resource: string) {
  let url
  let res
  let userCount: IUserCount = {
    teacher_count: 0,
    admin_count: 0,
    student_count: 0,
  }
  if (resource.toLowerCase() === 'district') {
    url = `/v1/organizations/usercount?district_id=${id}`
  } else {
    url = `/v1/organizations/usercount?school_id=${id}`
  }
  try {
    res = await getAPIRequest<IUserCountResponse>(url)
    if (res.error === undefined && Object.keys(res).length > 0) {
      userCount = res
    }
  } catch {}

  return userCount
}

export const scrollToBottom = () => {
  window.scroll({
    top: document.body.offsetHeight,
    left: 0,
    behavior: 'smooth',
  })
}

export const roleNameMap: { [key: string]: string } = {
  'INTERNAL admin': 'admin',
  'INTERNAL super': 'super-admin',
  'district-admin': 'district-admin',
  'school-admin': 'administrator',
  publisher: 'publisher',
  student: 'student',
  teacher: 'teacher',
  'school editor': 'editor',
  'demo teacher': 'demo-teacher',
  'district-editor': 'district-editor',
  'INTERNAL Data Wiping Admin': 'data-wiping-admin',
  'INTERNAL View Only Admin': 'view-only-admin',
  'INTERNAL impersonator': 'impersonator',
}

export const basicRoleNameMap: { [key: string]: string } = {
  student: 'student',
  teacher: 'teacher',
}

export const reverseMap = (map: { [key: string]: string }) => {
  const revRoleNameMap: { [key: string]: string } = {}
  for (const [key, value] of Object.entries(map)) {
    revRoleNameMap[value] = key
  }
  return revRoleNameMap
}

export const FEATURE_PACKAGES: any = {
  'Basic Feature Package': [
    'full-academy',
    'skill-should-forward-skill-service',
    'builder-pro',
    'group-chat',
    'curriculum-collaboration',
    'microsoft-onedrive-integration',
    'bookmarks',
  ],
  'Admin Reports': ['admin-reports'],
  'District Curriculum Editors': ['curriculum-editors'],
  'AI Features': [
    'ai-question-feedback',
    'ai-auto-scoring',
    'ai-practice-problems',
    'ai-lesson-clipper',
    'ai-curriculum-coach',
  ],
}

export const packageListStringFromFeatures = (
  platformFeatures: Single.PlatformFeature[],
) => {
  return transformFeaturesIntoPackages(platformFeatures)
    .filter(([_packageName, packageFeatures]) => {
      return packageFeatures.length > 0
    })
    .map(([packageName]) => packageName)
    .join(', ')
}

const matchingOrMissingPackages = (
  featureList: { name: string }[],
  matching: boolean,
) => {
  const currentFeatureNames = featureList.map((f: any) => f.name)
  return Object.entries(FEATURE_PACKAGES)
    .filter(([_packageName, packageFeatures]: any) => {
      const currentSet = new Set(currentFeatureNames)
      const relevantFeatures = packageFeatures.filter((f: string) =>
        matching ? currentSet.has(f) : !currentSet.has(f),
      )
      return relevantFeatures.length > 0
    })
    .map((featurePackage) => featurePackage[0])
}

export const findMissingOrIncompletePackages = (
  featureList: { name: string }[],
) => {
  return matchingOrMissingPackages(featureList, false)
}

export const findFullOrPartialMatchingPackages = (
  featureList: { name: string }[],
) => {
  return matchingOrMissingPackages(featureList, true)
}

const EXCLUDED_FEATURES = [
  'skill-correlation',
  'question-type-drawing',
  'question-type-classifying',
  'question-type-placeholder',
  'question-type-matching',
  'question-type-ordering',
  'question-type-short-answer',
]

export function transformFeaturesIntoPackages(
  platformFeatures: Single.PlatformFeature[],
) {
  const scrubbedFeatures = platformFeatures.filter(
    (individualFeature) => !EXCLUDED_FEATURES.includes(individualFeature.name),
  )
  const matchedFeaturePackages = Object.entries(FEATURE_PACKAGES).map(
    ([packageName, packageFeatures]: any) => {
      return [
        packageName,
        packageFeatures.reduce((acc: any, name: any) => {
          const matched = scrubbedFeatures.find((f: any) => f.name === name)
          return matched ? [...acc, matched] : [...acc]
        }, []),
      ]
    },
  )
  return matchedFeaturePackages
}

export function useNoPropagateHandler<T extends { stopPropagation(): void }>(
  handler?: (e: T) => void,
) {
  return useCallback(
    (e: T) => {
      e.stopPropagation()
      handler?.(e)
    },
    [handler],
  )
}

// convertExpirationDate converts expiration dates for demos
export const convertExpirationDate = (expirationDate: string) => {
  if (expirationDate === '') {
    return ''
  }
  //  expiration date comes in this format -> 2025-06-16 07:00:00
  const parsedDate = parse(expirationDate, 'yyyy-MM-dd HH:mm:ss', new Date())
  return format(parsedDate, 'yyyy-MM-dd')
}
